import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {mergeMap, withLatestFrom} from 'rxjs';
import {catchError, exhaustMap, map} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {RegisterUserDto} from '@server-models';
import {TechRegisterActions} from '@tech/pages/register/store/tech-register.actions-type';
import {TechRegisterApiService} from '@tech/pages/register/services/tech-register-api.service';
import {SharedLoginActions} from "@shared/components/login/store/shared-login.actions-type";
import {Router} from "@angular/router";
import {TechRegisterPinFormComponent} from "@tech/pages/register/components/pin-form/tech-register-pin-form.component";
import {SharedModalControllerService} from "@shared/services/shared-modal-controller.service";
import {SharedLoginSelectors} from "@shared/components/login/store/shared-login.selector-type";

@Injectable({
  providedIn: 'root',
})
export class TechRegisterEffects {
  private _store = inject(Store);
  private _actions$ = inject(Actions);
  private _router = inject(Router);
  private _techRegisterApiService = inject(TechRegisterApiService);
  private _modalControllerService = inject(SharedModalControllerService);


  // Registration Guest - Start -

  initRegistrationGuest$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TechRegisterActions.initRegistrationGuest),
      exhaustMap((action) => {
        return [TechRegisterActions.initRegistrationGuestDialogOpen({ currentEmail: action.currentEmail })];
      }),
      catchError((error) => [TechRegisterActions.initRegistrationGuestFail({ error })])
    )
  );

  initRegistrationGuestDialogOpen$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TechRegisterActions.initRegistrationGuestDialogOpen),
      exhaustMap((action: { currentEmail: string | undefined }) => {
        const controlEmail = action.currentEmail ? action.currentEmail : '';

        //this.loginTechSideEffectsService.askRegistrationData(controlEmail);
        return [TechRegisterActions.initRegistrationGuestSuccess()];
      })
    )
  );

  registerGuestPinRequest$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TechRegisterActions.registerGuestPinRequest),
      withLatestFrom(this._store.select(SharedLoginSelectors.selectBrand)),
      exhaustMap(([{ registerGuest }, brand]) => {
        return this._techRegisterApiService.requestGuestPin(registerGuest.email, brand).pipe(
          mergeMap(() => [
            TechRegisterActions.registerGuestPinRequestSuccess({
              isRetry: registerGuest.isRetry,
              email: registerGuest.email,
              passwordBase64: registerGuest.passwordBase64,
            }),
          ]),
          catchError((error) => [TechRegisterActions.registerGuestPinRequestFail({ error: error })])
        );
      })
    )
  );

  registerGuestPinRequestSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TechRegisterActions.registerGuestPinRequestSuccess),
      exhaustMap(({ isRetry, passwordBase64, email }) => {
        if (!isRetry) {
          this._modalControllerService.showModal(TechRegisterPinFormComponent, '', {
            passwordBase64,
            email
          });
        }
        return [TechRegisterActions.registerGuestPinDialog({})];
      })
    )
  );

  registerGuestAsUser$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TechRegisterActions.registerGuestAsUser),
      exhaustMap((action: { data: RegisterUserDto; isLoadingId: number | string; isRetry?: boolean }) => {
        return this._techRegisterApiService
          .requestGuestAsUser(action.data.pinFromEmail!, action.data.email!, action.data.passwordBase64!)
          .pipe(
            map(() => {
              return TechRegisterActions.registerGuestAsUserSuccess({
                isRetry: action.isRetry!,
                email: action.data.email!,
                passwordBase64: action.data.passwordBase64!,
              });
            }),
            catchError((error) => [TechRegisterActions.registerGuestAsUserFail({ error: error })])
          );
      })
    )
  );

  registerGuestAsUserSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TechRegisterActions.registerGuestAsUserSuccess),
      exhaustMap(({ email, passwordBase64 }) => {
        this._router.navigate(['/']).then(() => this._modalControllerService.closeModal());
        return [
          SharedLoginActions.login({
            email,
            passwordBase64
          }),
        ];
      })
    )
  );
}
